import { NIDownloadIcon, NIPreOrderIcon } from '@native-instruments/webex-ni';
import { hasDatePassed } from 'dotcom-core/src/helpers/date';
import { useRouteLoaderData } from 'react-router';
import type { AddressCountryResponse, ProductVariant } from '~/graphql';
import { useIntl } from '~/helpers/intl';
import { isTruthy } from '~/helpers/predicate';
import {
  getIsShippable,
  getReleaseDate,
  getStockStatus,
} from '~/helpers/product';
import type { ProductInterface } from '../route';

type Props = {
  variant?: ProductVariant | null;
  product: ProductInterface;
  countryRelatedInfo: AddressCountryResponse | null | undefined;
};

export function ProductAvailability({
  product,
  variant,
  countryRelatedInfo,
}: Props) {
  const { t, tDate } = useIntl();
  const root = useRouteLoaderData('root');

  const releaseDate = getReleaseDate({ product, variant });
  const releaseDatePassed = isTruthy(releaseDate) && hasDatePassed(releaseDate);
  const isPreOrder = isTruthy(releaseDate) && !isTruthy(releaseDatePassed);
  const isShippable = getIsShippable({ product, variant });
  const stockStatus = getStockStatus({ variant, product });

  const countryName =
    countryRelatedInfo?.countries?.find(
      (country) => country?.twoLetterAbbreviation === root.country,
    )?.fullNameLocale ?? '';

  const isFreeShipping =
    isTruthy(
      countryRelatedInfo?.freeShippingCountries?.includes(root.country),
    ) && isTruthy(countryName);

  return (
    <div data-testid="product-availability">
      {isPreOrder && (
        <div className="py-3 text-xs font-medium space-x-0.5 flex justify-center items-center">
          <NIPreOrderIcon />

          <p data-testid="preorder-msg">
            {!isShippable &&
              t('pages.pricing.preOrderMessage', {
                fallback: 'Download: Pre-order now and start using from {date}',
                values: {
                  date: tDate(releaseDate, {
                    year: 'numeric',
                    month: 'long',
                    day: '2-digit',
                  }),
                },
              })}

            {isShippable &&
              t('pages.pricing.preOrderMessageHW', {
                fallback: 'Pre-order now. Available from {date}',
                values: {
                  date: tDate(releaseDate, {
                    year: 'numeric',
                    month: 'long',
                    day: '2-digit',
                  }),
                },
              })}
          </p>
        </div>
      )}

      {!isPreOrder && !isShippable && (
        <div className="py-3 text-xs font-medium space-x-0.5 flex justify-center items-center text-success">
          <NIDownloadIcon />

          <p data-testid="download-available-msg">
            {t('components.ProductVariantInfo.downloadStock', {
              fallback: 'Download: Available immediately',
            })}
          </p>
        </div>
      )}

      {!isPreOrder && isShippable && (
        <div className="text-xs font-medium py-3">
          {stockStatus === 'IN_STOCK' && (
            <div
              className="flex items-center space-x-1"
              data-testid="in-stock-status"
            >
              <div className="w-2 h-2 bg-success rounded-full" />
              <p className="text-success">
                {t('components.ProductVariantInfo.inStock.title', {
                  fallback: 'In stock',
                })}
              </p>
            </div>
          )}

          {stockStatus === 'OUT_OF_STOCK' && (
            <div
              className="flex items-center space-x-1"
              data-testid="out-of-stock-status"
            >
              <div className="w-2 h-2 bg-error rounded-full" />
              <p className="text-error">
                {t('components.ProductVariantInfo.outOfStock.title', {
                  fallback: 'Out of stock',
                })}
              </p>
            </div>
          )}

          {isFreeShipping && (
            <div className="mt-2">
              <p
                data-testid="free-shipping-message"
                className="mb-0.5 text-sm font-medium"
              >
                {t('pdp.shippingInfo', {
                  fallback: 'Free shipping to {country}.',
                  values: {
                    country: countryName,
                  },
                })}
              </p>
              <p
                className="text-xs text-grey-60 font-regular"
                data-testid="usually-ships-message"
              >
                {t('components.ProductVariantInfo.inStock.description', {
                  fallback: 'Usually ships in 1-2 business days.',
                })}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
